import {
    Autocomplete,
    Box, createFilterOptions, Input,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow, TextField,
    Tooltip, Typography
} from "@mui/material";
import React, {useEffect, useState, useMemo} from "react";
import {useDebounce} from "../../../../hooks/useDebounce";
import {ConversationTableRow} from "./ConversationTableRow";
import DateRangePicker from "../../../../components/date-range/DateRangePicker";
import {useAtom} from "jotai";
import {SelectedConversationAtom} from "../atoms";
import {useDashboard} from "../../../../context/DashboardContext";
import {HugeiconsIcon} from "@hugeicons/react";
import {Message02Icon, PhoneIcon} from "@hugeicons/core-free-icons";
import {grey} from "@mui/material/colors";

const filterFieldSx = {
    borderRadius: 2,
    px: 1,
    minHeight: 28,
    display: "flex",
    alignItems: "center",
    fontSize: 11,
    bgcolor: "grey.200",
};

export const ConversationsTable = ({project}) => {
    const {conversations, pagination, setPagination, refreshConversations, updatedValue} = useDashboard();
    const [selectedConversation, setSelectedConversation] = useAtom(SelectedConversationAtom);
    const [filters, setFilters] = useState({
        id: "",
        start: null,
        end: null,
        caller: "",
        status: null,
        vdn: "",
        tags: [],
    })
    const filtersDebounced = useDebounce(filters, 500);
    const phoneBook = useMemo(() => project?.phonebook || [], [project?.phonebook]);
    const phoneBookOptions = Object.entries(phoneBook).map(([phone, name]) => ({
        phone,
        name
    }));
    const phoneBookFilterOptions = createFilterOptions({
        stringify: (option) => `${option?.name || ""} ${option?.phone || ""}`
    });

    useEffect(() => {
        if (conversations && conversations?.length && selectedConversation?.id) {
            const conversation = conversations?.find(i => i?.id === selectedConversation?.id);
            if (conversation) {
                setSelectedConversation(conversation);
            }
        }
    }, [conversations, selectedConversation?.id])

    useEffect(() => {
        if (project?.id) {
            const getDate = date => {
                return date.getTime();
            }

            const formattedFilters = {
                ...filtersDebounced,
                start: filtersDebounced?.start ? getDate(filtersDebounced?.start) : undefined,
                end: filtersDebounced?.end ? getDate(filtersDebounced?.end) : undefined,
                tagIds: filtersDebounced?.tags?.length ? filtersDebounced.tags.map(t => t?.id) : undefined,
            };

            refreshConversations(formattedFilters);

            const interval = setInterval(() => {
                refreshConversations(formattedFilters);
            }, 60 * 1000);

            return () => clearInterval(interval);
        }
    }, [project?.id, pagination?.page, pagination?.size, filtersDebounced, updatedValue, refreshConversations])

    const scrollConversations = (elementId = "conversation-tbody") => {
        const tbody = document?.getElementById(elementId);
        if (tbody) {
            tbody?.scrollIntoView({behavior: "smooth", block: "end"})
        } else {
            throw new Error("Element not found")
        }
    }

    const type = project?.settings?.projectType || "VOICE_BOT"

    return (
        <Stack direction="column" gap={0} sx={{borderRadius: 3, height: "100%", minHeight: 0}}
               className={"border-shadow"}>
            <TableContainer sx={{flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto"}}>
                <Table stickyHeader aria-label="sticky table" sx={{ p: 2, pt: 1, tableLayout: "fixed", minWidth: 700}}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding={"checkbox"} sx={{py: 0.5, px: 1, width: {xs: 84, md: 60}, maxWidth: {xs: 84, md: 60}}}>
                                <Input
                                    className={"table-input"}
                                    disableUnderline
                                    sx={{...filterFieldSx, width: "100%"}}
                                    value={filters?.id} placeholder={"ID"}
                                    onChange={e => setFilters(prev => ({...prev, id: e?.target?.value}))}
                                />
                            </TableCell>

                            <TableCell padding={"checkbox"}
                                       sx={{py: 0.5, px: 1, minWidth: 160, display: "table-cell"}}>
                                <Autocomplete
                                    freeSolo
                                    value={filters?.callerContact || filters?.caller || null}
                                    onChange={(event, newValue) => {
                                        if (typeof newValue === "string") {
                                            setFilters(prev => ({...prev, caller: newValue, callerContact: null}));
                                        } else {
                                            setFilters(prev => ({
                                                ...prev,
                                                caller: newValue?.phone || "",
                                                callerContact: newValue
                                            }));
                                        }
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === "input") {
                                            setFilters(prev => ({...prev, caller: newInputValue, callerContact: null}));
                                        }
                                    }}
                                    options={phoneBookOptions}
                                    getOptionLabel={(option) => {
                                        if (typeof option === "string") return option;
                                        return option?.name || "";
                                    }}
                                    filterOptions={phoneBookFilterOptions}
                                    isOptionEqualToValue={(option, value) => option?.phone === value?.phone}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{fontSize: 12, minHeight: "auto", padding: "4px 8px"}}>
                                            {typeof option === "string" ? option : option?.name || ""}
                                        </li>
                                    )}
                                    clearOnEscape
                                    className={"table-input"}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Caller"
                                            className={"table-input"}
                                            variant="standard"
                                            InputProps={{
                                                ...params.InputProps,
                                                disableUnderline: true,
                                                sx: {border: 0, fontSize: 11}
                                            }}
                                        />
                                    )}
                                    sx={filterFieldSx}
                                />
                            </TableCell>
                            <TableCell padding={"checkbox"}
                                       sx={{py: 0.5, px: 0.5, width: 88, maxWidth: 88, display: {xs: "none", md: "table-cell"}}}>
                                <DateRangePicker
                                    startDate={filters?.start}
                                    endDate={filters?.end}
                                    onStartChange={(d) => setFilters((prev) => ({...prev, start: d}))}
                                    onEndChange={(d) => setFilters((prev) => ({...prev, end: d}))}
                                    triggerSx={{
                                        ...filterFieldSx,
                                        width: "100%",
                                        minWidth: 0,
                                        justifyContent: "flex-start",
                                        gap: 1,
                                        fontWeight: 400,
                                        lineHeight: 1.4,
                                        textTransform: "none",
                                        color: "text.primary",
                                        boxShadow: "none",
                                        "&:hover": {bgcolor: "grey.300", boxShadow: "none"},
                                    }}
                                />
                            </TableCell>

                            <TableCell padding={"checkbox"} sx={{
                                py: 0.5, px: 1, width: {xs: 96, md: type === "CHAT_BOT" ? 30 : 90},
                                maxWidth: {xs: 96, md: type === "CHAT_BOT" ? 30 : 90}
                            }}>
                                <Autocomplete
                                    value={filters.status || null}
                                    onChange={(event, newValue) => {
                                        setFilters(prev => ({...prev, status: newValue}));
                                    }}
                                    options={type === "VOICE_BOT"
                                        ? ["ACTIVE", "FINISHED", "REDIRECTED", "FAILED", "HANDOVER", "HANGUP", "MISSED_CALL"]
                                        : ["ACTIVE", "FINISHED", "FAILED"]}
                                    clearOnEscape
                                    freeSolo={false}
                                    className={"table-input"}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Status"
                                            className={"table-input"}
                                            variant="standard"
                                            InputProps={{
                                                ...params.InputProps,
                                                disableUnderline: true,
                                                sx: {border: 0, fontSize: 11}
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{fontSize: 14, minHeight: "auto", padding: "4px 8px"}}>
                                            {option}
                                        </li>
                                    )}
                                    sx={filterFieldSx}
                                />
                            </TableCell>
                            <TableCell padding={"checkbox"}
                                       sx={{py: 0.5, px: 1, textWrap: "nowrap", width: "86px", maxWidth: "86px", display: {xs: "none", md: "table-cell"}}}>

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    value={filters?.tags || []}
                                    onChange={(event, newValue) => {
                                        setFilters(prev => ({...prev, tags: newValue}));
                                    }}
                                    options={project?.settings?.tags || []}
                                    getOptionLabel={(option) => option?.title || option?.name || ""}
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    clearOnEscape
                                    className={"table-input"}
                                    renderTags={(value) => (
                                        <Tooltip
                                            title={value?.length ? value.map(t => t?.title || t?.name).join(", ") : ""}
                                            arrow
                                            placement="bottom-start"
                                        >
                                            <Box component="span" sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 18,
                                                height: 18,
                                                minWidth: 18,
                                                maxWidth: 18,
                                                minHeight: 18,
                                                maxHeight: 18,
                                                borderRadius: "50%",
                                                bgcolor: "primary.main",
                                                color: "primary.contrastText",
                                                fontSize: 12,
                                                fontWeight: 500,
                                                lineHeight: 1,
                                                cursor: "default",
                                                flexShrink: 0,
                                                boxSizing: "border-box"
                                            }}>
                                                {value?.length || ""}
                                            </Box>
                                        </Tooltip>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={filters?.tags?.length ? "" : "Tags"}
                                            className={"table-input"}
                                            variant="standard"
                                            InputProps={{
                                                ...params.InputProps,
                                                disableUnderline: true,
                                                sx: {border: 0, fontSize: 11, flexWrap: "nowrap"}
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{fontSize: 14, minHeight: "auto", padding: "4px 8px"}}>
                                            {option?.title || option?.name || ""}
                                        </li>
                                    )}
                                    sx={{
                                        ...filterFieldSx,
                                        fontSize: 10,
                                        "& .MuiAutocomplete-inputRoot": {
                                            flexWrap: "nowrap",
                                            overflow: "hidden"
                                        }
                                    }}
                                />
                            </TableCell>

                            <TableCell padding={"checkbox"} sx={{
                                py: 0.5,
                                px: 0,
                                width: 32,
                                maxWidth: 32,
                                display: {xs: "none", md: "table-cell"}
                            }}></TableCell>


                            {type === "VOICE_BOT" ?
                                <TableCell padding={"checkbox"} sx={{py: 0.5, px: 0, display: {xs: "none", md: "table-cell"}}}></TableCell> : null}
                        </TableRow>
                    </TableHead>
                    <TableBody id={"conversation-tbody"}>
                        {conversations?.length > 0 ? (
                            conversations?.map(item => (
                                <ConversationTableRow
                                    key={item?.id} conversation={item} project={project}
                                />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={type === "VOICE_BOT" ? 7 : 6} sx={{border: 0}}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        py: 6,
                                        color: "grey.500",
                                        flexDirection: "column",
                                        gap: 1,
                                    }}>
                                        {type === "VOICE_BOT" ?
                                            <HugeiconsIcon icon={PhoneIcon} size={48} color={grey[300]}/> :
                                            <HugeiconsIcon icon={Message02Icon} size={48} color={grey[300]}/>
                                        }
                                        <Typography variant="body1" color={grey[500]}>No conversations found</Typography>
                                        <Typography variant="body2" color={grey[400]}>Try adjusting your filters</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Stack direction={"row"} sx={{width: "100%"}} justifyContent={"flex-end"}>
                <TablePagination
                    component="div"
                    count={pagination?.totalElements}
                    page={pagination?.page}
                    onPageChange={(e, page) => setPagination(prev => ({...prev, page: page}))}
                    rowsPerPage={pagination?.size}
                    onRowsPerPageChange={(e) => setPagination(prev => ({...prev, page: 0, size: +e.target.value}))}
                />
            </Stack>
        </Stack>
    )
}
